/**
 * Shared emergency alarm + background-tab notifications for the hospital dashboard.
 * - Web Audio siren (with AudioContext.resume for browser autoplay policy)
 * - Browser Notification API when tab is hidden
 * - Document title flash
 * - Unified mute state (localStorage)
 */

const MUTE_KEY = 'emergencySoundMuted';
const TITLE_KEY = '__hcen_orig_title';

let audioCtx = null;
let sirenOsc = null;
let sweepTimer = null;
let buzzerTimer = null;
let soundActive = false;
let titleFlashTimer = null;
let notifPermissionRequested = false;

export function isEmergencySoundMuted() {
  return localStorage.getItem(MUTE_KEY) === 'true';
}

export function setEmergencySoundMuted(muted) {
  localStorage.setItem(MUTE_KEY, muted ? 'true' : 'false');
  if (muted) stopEmergencyAlarm();
}

/** Unlock AudioContext — call after any user gesture (login click, etc.). */
export async function unlockEmergencyAudio() {
  try {
    if (!audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return false;
      audioCtx = new Ctx();
    }
    if (audioCtx.state === 'suspended') await audioCtx.resume();
    return audioCtx.state === 'running';
  } catch (_) {
    return false;
  }
}

export function stopEmergencyAlarm() {
  soundActive = false;
  clearInterval(sweepTimer);
  clearInterval(buzzerTimer);
  sweepTimer = null;
  buzzerTimer = null;
  try { sirenOsc?.stop(); } catch (_) {}
  sirenOsc = null;
  try {
    if (audioCtx?.state !== 'closed') audioCtx?.close();
  } catch (_) {}
  audioCtx = null;
  stopTitleFlash();
}

export async function startEmergencyAlarm() {
  if (soundActive || isEmergencySoundMuted()) return;
  try {
    await unlockEmergencyAudio();
    if (!audioCtx || audioCtx.state !== 'running') return;

    soundActive = true;
    const ctx = audioCtx;
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.45, ctx.currentTime);
    masterGain.connect(ctx.destination);

    const siren = ctx.createOscillator();
    siren.type = 'sawtooth';
    siren.frequency.setValueAtTime(720, ctx.currentTime);
    sirenOsc = siren;
    const sirenGain = ctx.createGain();
    sirenGain.gain.setValueAtTime(0.55, ctx.currentTime);
    siren.connect(sirenGain);
    sirenGain.connect(masterGain);
    siren.start();

    const doSweep = () => {
      if (!audioCtx || !soundActive) return;
      const now = audioCtx.currentTime;
      siren.frequency.cancelScheduledValues(now);
      siren.frequency.setValueAtTime(720, now);
      siren.frequency.linearRampToValueAtTime(960, now + 0.45);
      siren.frequency.linearRampToValueAtTime(720, now + 0.90);
    };
    doSweep();
    sweepTimer = setInterval(doSweep, 900);

    buzzerTimer = setInterval(() => {
      if (!audioCtx || !soundActive) return;
      const bCtx = audioCtx;
      const bOsc = bCtx.createOscillator();
      const bGain = bCtx.createGain();
      bOsc.type = 'square';
      bOsc.frequency.setValueAtTime(1400, bCtx.currentTime);
      bGain.gain.setValueAtTime(0.18, bCtx.currentTime);
      bGain.gain.exponentialRampToValueAtTime(0.001, bCtx.currentTime + 0.08);
      bOsc.connect(bGain);
      bGain.connect(masterGain);
      bOsc.start();
      bOsc.stop(bCtx.currentTime + 0.09);
    }, 450);
  } catch (e) {
    console.warn('[HCEN] Emergency alarm failed:', e);
    soundActive = false;
  }
}

export function isEmergencyAlarmActive() {
  return soundActive;
}

/** Request notification permission once (after login / first click). */
export async function ensureEmergencyNotificationPermission() {
  if (notifPermissionRequested) return Notification.permission;
  if (typeof Notification === 'undefined') return 'denied';
  notifPermissionRequested = true;
  if (Notification.permission === 'default') {
    try {
      return await Notification.requestPermission();
    } catch (_) {
      return 'denied';
    }
  }
  return Notification.permission;
}

/** Show OS notification when tab is in background. */
export function notifyEmergencyInBackground(alerts = []) {
  if (typeof document === 'undefined') return;
  if (document.visibilityState !== 'hidden') return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

  const count = alerts.length;
  if (!count) return;

  const latest = alerts[0] || {};
  const type = latest.emergencyType || 'Emergency';
  const patient = latest.patientName || 'Patient';
  const body = count > 1
    ? `${count} SOS alerts need immediate action`
    : `${patient} — ${type}. Tap to open Emergency Alerts.`;

  try {
    const n = new Notification('🚨 HCEN Emergency SOS', {
      body,
      tag: 'hcen-emergency-sos',
      requireInteraction: true,
      silent: false,
    });
    n.onclick = () => {
      window.focus();
      window.location.hash = '';
      window.location.pathname = '/emergency-alerts';
      n.close();
    };
  } catch (e) {
    console.warn('[HCEN] Notification failed:', e);
  }
}

export function startTitleFlash(count) {
  if (typeof document === 'undefined') return;
  if (!document[TITLE_KEY]) document[TITLE_KEY] = document.title;
  stopTitleFlash();
  let on = true;
  titleFlashTimer = setInterval(() => {
    document.title = on
      ? `🚨 ${count} SOS ALERT${count > 1 ? 'S' : ''}!`
      : document[TITLE_KEY];
    on = !on;
  }, 1000);
}

export function stopTitleFlash() {
  clearInterval(titleFlashTimer);
  titleFlashTimer = null;
  if (typeof document !== 'undefined' && document[TITLE_KEY]) {
    document.title = document[TITLE_KEY];
  }
}
